<template>
  <div class="layout_box">
    <Teleport to="body">
      <DynamicWatermark :content="['自然源数字', userStore.nickname + '' + currentTime]" />
    </Teleport>

    <div
      :class="classObj"
      class="app-wrapper"
      :style="{ '--current-color': theme }"
    >
      <navbar
        ref="navbarRef"
        @set-layout="setLayout"
      />
      <div
        v-if="device === 'mobile' && sidebar.opened"
        class="drawer-bg"
        @click="handleClickOutside"
      />
      <div class="centerMianBox">
        <side-bar
          v-if="!sidebar.hide"
          class="sidebar-container"
        />
        <div
          :class="{ hasTagsView: needTagsView, sidebarHide: sidebar.hide }"
          class="main-container"
        >
          <hamburger
            v-if="!appStore.sidebar.opened"
            id="hamburger-container"
            :is-active="appStore.sidebar.opened"
            class="hamburger-container"
            @toggle-click="toggleSideBar"
          />
          <div :class="{ 'fixed-header': fixedHeader }">
            <tags-view v-if="needTagsView" />
          </div>
          <div
            ref="pageCenterHei"
            class="pageCneterBox"
          >
            <app-main />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from '@/store/modules/app';
import SideBar from './components/Sidebar/index.vue';
import { DEFAULT_PASSWORD_REMINDER_KEY_PREFIX, useUserStore } from '@/store/modules/user';
import { AppMain, Navbar, Settings, TagsView } from './components';
import { useSettingsStore } from '@/store/modules/settings';
import DynamicWatermark from './components/DynamicWatermark.vue';
import { initWebSocket } from '@/utils/websocket';
import { checkDefaultPasswordApi } from '@/api/brand/user';
import router from '@/router';
const appStore = useAppStore();
const pageCenterHei = ref(null);
const settingsStore = useSettingsStore();
const theme = computed(() => settingsStore.theme);
const sidebar = computed(() => useAppStore().sidebar);
const device = computed(() => useAppStore().device);
const currentTime = ref('');
const userStore = useUserStore(); // 你的动态文字
const { proxy } = getCurrentInstance() as ComponentInternalInstance;
const needTagsView = computed(() => settingsStore.tagsView);
const fixedHeader = computed(() => settingsStore.fixedHeader);
const classObj = computed(() => ({
  hideSidebar: !sidebar.value.opened,
  openSidebar: sidebar.value.opened,
  withoutAnimation: sidebar.value.withoutAnimation,
  mobile: device.value === 'mobile',
}));

const { width } = useWindowSize();
const WIDTH = 992; // refer to Bootstrap's responsive design

watchEffect(() => {
  if (device.value === 'mobile') {
    useAppStore().closeSideBar({ withoutAnimation: false });
  }
  if (width.value - 1 < WIDTH) {
    useAppStore().toggleDevice('mobile');
    useAppStore().closeSideBar({ withoutAnimation: true });
  } else {
    useAppStore().toggleDevice('desktop');
  }
});

const navbarRef = ref<InstanceType<typeof Navbar>>();
const settingRef = ref<InstanceType<typeof Settings>>();

/** 当前账号在本次登录会话内仅提醒一次默认密码风险。 */
const showDefaultPasswordWarning = async () => {
  if (!userStore.userId || !userStore.tenantId) return;

  const reminderKey = `${DEFAULT_PASSWORD_REMINDER_KEY_PREFIX}${userStore.userId}:${userStore.tenantId}`;
  if (sessionStorage.getItem(reminderKey)) return;

  try {
    const res = await checkDefaultPasswordApi();
    if (res.data !== true) return;
  } catch {
    // 默认密码检查失败时静默处理，不影响页面使用。
    return;
  }

  sessionStorage.setItem(reminderKey, '1');
  try {
    await ElMessageBox.confirm(
      proxy.$t('AccountCenter.defaultPasswordWarningMessage'),
      proxy.$t('AccountCenter.defaultPasswordWarningTitle'),
      {
        confirmButtonText: proxy.$t('AccountCenter.changePasswordNow'),
        cancelButtonText: proxy.$t('Shared.Close'),
        type: 'warning',
      },
    );
  } catch {
    return;
  }

  await nextTick();
  await router.push({ name: 'AccountCenterPassword' });
};

onMounted(() => {
  showDefaultPasswordWarning();
  formatTime();
  setInterval(() => {
    formatTime();
  }, 5000);
  nextTick(() => {
    navbarRef.value?.initTenantList();
    window.addEventListener('resize', handleResize);
    useAppStore().SetAppMainHei(pageCenterHei.value.offsetHeight);
  });
});
const handleResize = () => {
  useAppStore().SetAppMainHei(pageCenterHei.value.offsetHeight);
};

const handleClickOutside = () => {
  useAppStore().closeSideBar({ withoutAnimation: false });
};

const setLayout = () => {
  settingRef.value?.openSetting();
};
const toggleSideBar = () => {
  appStore.toggleSideBar(false);
};
// 1. 定义一个响应式变量来存储格式化后的时间字符串

// 2. 定义一个函数，用于格式化 Date 对象为 "YYYY-MM-DD HH:mm:ss" 格式
const formatTime = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() 返回 0-11
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0'); // 新增：获取秒并补零
  // 返回数字格式，如：2023-10-27 15:30:45
  if (currentTime.value != `${year}年${month}月${day}日 ${hours}:${minutes}`) {
    currentTime.value = `${year}年${month}月${day}日 ${hours}:${minutes}`;
  }
};
</script>
<style scoped>
/* 1️⃣ 父容器：必须设置 relative，水印才能铺满它 */
.parent-box {
  position: relative;
  width: 100%;
  min-height: 500px; /* 父元素高度（你页面多高就多高） */
  border: 1px solid #eee;
}

/* 2️⃣ 内容层：在水印上方 */
.content {
  position: relative;
  z-index: 999999;
}

/* 3️⃣ 核心：水印div → 铺满整个父元素 + 平铺水印 */
.watermark {
  /* 🔥 关键：铺满父元素的终极写法 */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* 🔥 铺满结束 */

  z-index: 0;
  pointer-events: none; /* 不阻挡点击 */
  font-weight: 400; /* ✅ 你要的字重400 */
  font-size: 16px;
  color: #000;

  /* 🔥 水印重复铺满（纯CSS，火狐永不闪动） */
  background-image: repeating-linear-gradient(transparent 0px, transparent 120px, #fff 120px, #fff 121px);
  background-size: 180px 180px; /* 水印间距 */
  transform: rotate(-15deg);
}

/* 🔥 生成重复水印文字 */
.watermark::before {
  content: attr(data-text);
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  line-height: 180px;
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/mixin.scss';
@use '@/assets/styles/variables.module.scss' as *;
.centerMianBox {
  display: flex;
  width: 100vw;
  margin-top: 10px;
}

.pageCneterBox {
  overflow: hidden;
}

.hamburger-container {
  position: fixed;
  top: 90px;
  z-index: 99999;

  // left: 0;
  inset-inline-start: 0;
}

.layout_box {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-image: url(../assets/images/bg.png);
  position: relative;
  font-family:
    Helvetica Neue,
    Helvetica,
    PingFang SC,
    Hiragino Sans GB,
    Microsoft YaHei,
    微软雅黑,
    Arial,
    sans-serif !important;
}

.app-wrapper {
  @include mixin.clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  inset-inline-end: 0;
  z-index: 9;
  width: calc(100% - #{$base-sidebar-width});
  transition: width 0.28s;
  background: $fixed-header-bg;
}

.hideSidebar .fixed-header {
  width: calc(100% - 54px);
}

.sidebarHide .fixed-header {
  width: 100%;
}

.mobile .fixed-header {
  width: 100%;
}

@media (max-width: 992px) {
  .mobile .hamburger-container {
    // 移动端将展开按钮对齐到标签栏中线，避免图标偏上。
    top: 80px;
    inset-inline-start: 8px;
  }

  .mobile .main-container :deep(#tags-view-container .tags-view-item:first-of-type) {
    // 移动端汉堡按钮位于标签栏左侧，首个标签需要避开按钮区域。
    margin-inline-start: 55px;
  }
}
</style>
